import { alertDisplay, resolveCompanyBrand } from '../../lib/alertCopy';
import { flagMeaning, TAG_TONES } from '../../lib/colorMeaning';
import { formatPhone } from '../../lib/phone';
import { formatBellTime, notificationThreadUrl } from '../../lib/notificationInbox';

// BL-177 Step 2d: what one bell row / pop-up shows. Same title and preview as the push (src/lib/alertCopy.js).

/** The small icon tile on a row: what kind of alert it is (no colour; the word is the accessible name). */
export function notificationKind(item) {
    const eventType = String(item?.eventType || '');
    if (eventType === 'staff.teams_message') return { icon: 'bell', label: 'Teams' };
    if (eventType === 'staff.inbound_message') {
        const channel = String(item?.payload?.channel || 'sms').toLowerCase();
        return channel === 'whatsapp' ? { icon: 'whatsapp', label: 'WhatsApp' } : channel === 'email' ? { icon: 'email', label: 'Email' } : { icon: 'sms', label: 'SMS' };
    }
    if (/rating/.test(eventType)) return { icon: 'star', label: 'Rating' };
    if (/health|critical|dead_zone|overdue/.test(eventType)) return { icon: 'alert', label: 'Alert' };
    return { icon: 'bell', label: 'Notification' };
}

/** The colour flag chip, only when relevant: conversation flag, After hours, or the alert's severity. Always words. */
export function notificationChip(item, fields = {}) {
    const flag = flagMeaning(item?.payload?.flag || item?.payload?.flagKey);
    if (flag) return { tone: flag.tone, label: flag.label };
    if (fields.afterHours) return { tone: TAG_TONES.after_hours.tone, label: TAG_TONES.after_hours.label };
    const severity = String(item?.payload?.alertSeverity || item?.payload?.severity || '');
    if (severity === 'critical') return { tone: 'red', label: 'Act now' };
    if (severity === 'high' || severity === 'warning') return { tone: 'orange', label: 'Needs attention' };
    return null;
}

/** `brands` = { entities, numbers } from fetchAlertBrands, used when the payload doesn't name the company. */
export function notificationItemView(item, now = new Date(), brands = null) {
    const isAlert = String(item?.eventType || '').startsWith('staff.');
    const companyName = brands && !item?.payload?.companyName
        ? resolveCompanyBrand({ entities: brands.entities, numbers: brands.numbers, number: item?.payload?.number }).companyName
        : '';
    const display = isAlert
        ? alertDisplay(item.eventType, item.payload, companyName ? { companyName } : {}, { formatPhone })
        : { title: item?.title || 'Notification', preview: item?.body || '', fields: {} };
    return {
        title: display.title || item?.title || 'Notification',
        preview: display.preview || '',
        time: formatBellTime(item?.createdAt, now),
        url: notificationThreadUrl(item),
        kind: notificationKind(item),
        chip: notificationChip(item, display.fields),
    };
}

/** Critical severity: the alert that gets through quiet hours and always uses the urgent tone. */
export function isCriticalAlert(item) {
    return String(item?.payload?.alertSeverity || item?.payload?.severity || '') === 'critical';
}

/** Urgent tone: critical severity, or a red "Act now" alert (a red conversation flag such as Urgent / ASAP). */
export function isUrgentAlert(item) {
    return isCriticalAlert(item) || notificationChip(item, {})?.tone === 'red';
}

/**
 * BL-177 Step 2o (section 4): what ONE pop-up card shows when it holds several alerts. `items` is oldest first; the latest alert gives the
 * preview, the link and the colour chip. Texts from one conversation read "3 new messages from Roger Dodger".
 */
export function notificationGroupView(items = [], now = new Date(), brands = null) {
    const latest = items[items.length - 1];
    const view = notificationItemView(latest, now, brands);
    if (items.length < 2) return { ...view, count: 1 };
    const isText = String(latest?.eventType || '') === 'staff.inbound_message';
    const who = String(latest?.payload?.contactName || '').trim();
    const title = isText
        ? `${items.length} new messages${who ? ` from ${who}` : ''}`
        : `${items.length} new notifications`;
    return { ...view, title, count: items.length };
}
